import { Pause, Play, Volume2, VolumeX } from "lucide-react";
import { useVoice } from "../../contexts/VoiceContext";
import { Button } from "./button";

interface VoiceButtonProps {
    text: string;
    variant?: "default" | "ghost" | "outline";
    size?: "default" | "sm" | "lg" | "icon";
    className?: string;
    showIcon?: boolean;
    disabled?: boolean;
}

export function VoiceButton({
    text,
    variant = "ghost",
    size = "default",
    className,
    showIcon = true,
    disabled = false,
}: VoiceButtonProps) {
    const { state, toggle } = useVoice();

    if (!state.isSupported) {
        return null;
    }

    const isCurrentText = state.currentText === text;
    const isPlaying = isCurrentText && state.isPlaying;
    const isPaused = isCurrentText && state.isPaused;
    const isSomethingElsePlaying = state.isPlaying && !isCurrentText;

    const handleClick = () => {
        if (disabled) return;
        toggle(text);
    };

    const getIcon = () => {
        if (!showIcon) return null;

        if (isPlaying) {
            return <Pause className="h-4 w-4" />;
        } else if (isPaused) {
            return <Play className="h-4 w-4" />;
        } else {
            return <Volume2 className="h-4 w-4" />;
        }
    };

    const getTitle = () => {
        if (isPlaying) {
            return "Pause narration";
        } else if (isPaused) {
            return "Resume narration";
        } else if (isSomethingElsePlaying) {
            return "Switch to this narration";
        } else {
            return "Play narration";
        }
    };

    return (
        <Button
            variant={variant}
            size={size}
            onClick={handleClick}
            disabled={disabled}
            className={`${className} ${isSomethingElsePlaying ? "opacity-60 hover:opacity-100" : ""} text-primary`}
            title={getTitle()}
            aria-label={getTitle()}
        >
            {getIcon()}
        </Button>
    );
}

interface VoiceMuteButtonProps {
    variant?: "default" | "ghost" | "outline";
    size?: "default" | "sm" | "lg" | "icon";
    className?: string;
}

export function VoiceMuteButton({ variant = "ghost", size = "default", className }: VoiceMuteButtonProps) {
    const { state, stop, updateSettings } = useVoice();

    if (!state.isSupported) {
        return null;
    }

    const isMuted = state.settings.volume === 0;

    const handleToggleMute = () => {
        if (isMuted) {
            updateSettings({ volume: 0.8 });
        } else {
            updateSettings({ volume: 0 });
            stop();
        }
    };

    return (
        <Button
            variant={variant}
            size={size}
            onClick={handleToggleMute}
            className={className}
            title={isMuted ? "Unmute voice" : "Mute voice"}
            aria-label={isMuted ? "Unmute voice" : "Mute voice"}
        >
            {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </Button>
    );
}
